import { CommonModule } from '@angular/common'
import { Component, Input } from '@angular/core'
import { DestroySubscribers, FloatingTab, FloatingTabsColorSet, FloatingTabsComponent } from '@ftr/foundation'
import { Observable, distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs'
import { ComplexFilterService, ComplexFilterValue, isEqualComplexFilterValue } from '../../complex-filter'
import { RadioListFilter } from '../../complex-filter/RadioListFilter'

/**
 * This component replaces the status type part of the complex filter component on large screens.
 * Doesn't handle filtering or query parameters, communicates all the changes through the service.
 */
@Component({
  selector: 'ftr-complex-list-status-type',
  templateUrl: './complex-list-status-type.component.html',
  standalone: true,
  imports: [CommonModule, FloatingTabsComponent],
})
export class ComplexListStatusTypeComponent<StatusType extends string = string> extends DestroySubscribers {
  @Input() config: RadioListFilter<StatusType>
  @Input() tabs: FloatingTab<StatusType>[]

  readonly floatingTabsColorSet = FloatingTabsColorSet
  statusType$: Observable<StatusType>

  private dependentFilterValue: ComplexFilterValue | null
  private canUpdateFilter = true

  constructor(private readonly complexFilterService: ComplexFilterService) {
    super()
  }

  ngOnInit(): void {
    this.statusType$ = this.complexFilterService.listenToFilterChange([this.config]).pipe(
      takeUntil(this.finalize),
      map(filters => filters[this.config.filterKey] as StatusType),
    )

    this.setupDependentFilter()
  }

  async onFilterSelectionChange(value: StatusType | null): Promise<void> {
    if (value) {
      await this.complexFilterService.updateFilter({ [this.config.filterKey]: value })
    }
  }

  private setupDependentFilter(): void {
    if (!this.config.dependentFilter) {
      return
    }

    const { association, catchAllValue, filter: depFilter } = this.config.dependentFilter
    const filterKey = depFilter?.filterKey

    if (!association || !catchAllValue || !depFilter || !filterKey) {
      return
    }

    /**
     * We listen to this status type to ensure we catch updates from different sources –
     * modal on mobile and this component on desktop.
     */
    this.statusType$
      .pipe(
        tap(async statusType => {
          if (this.canUpdateFilter) {
            this.dependentFilterValue = association[statusType]
            await this.complexFilterService.updateFilter(
              { [filterKey]: this.dependentFilterValue },
              // It's not a real status change, don't reset the page
              { resetPage: false },
            )
          }

          this.canUpdateFilter = true
        }),
      )
      .subscribe()

    this.complexFilterService
      .listenToFilterChange([depFilter])
      .pipe(
        takeUntil(this.finalize),
        map(fltr => fltr[filterKey]),
        distinctUntilChanged(isEqualComplexFilterValue),
        filter(value => !isEqualComplexFilterValue(value, this.dependentFilterValue)),
        tap(async () => {
          // If this value was set by the status type change above, do not trigger another update
          if (this.dependentFilterValue) {
            this.canUpdateFilter = false
          }

          await this.complexFilterService.updateFilter(
            { [this.config.filterKey]: catchAllValue },
            // It's not a real status change, don't reset the page
            { resetPage: false },
          )
        }),
      )
      .subscribe()
  }
}
